import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { fetchWithAuth } from "../lib/api";
import OperationalBand from "./admin/OperationalBand";
import AdminTruthBand from "./admin/AdminTruthBand";
import InstagramDmLedgerCard from "./admin/InstagramDmLedgerCard";
import FleetActionsBand from "./admin/FleetActionsBand";
import FleetActionsModal from "./admin/FleetActionsModal";
import { type FleetActions } from "./admin/fleetActionsFormat";
import { type BoxVitals, type FixQueueRow } from "./admin/adminBandFormat";
import { numericQueryTruth } from "./ops/adminTruthState";
import { usePersistedState } from "../hooks/usePersistedState";
import PageHeaderTitle from "./PageHeaderTitle";
import PageRefreshButton from "./PageRefreshButton";
import { LoadingTopBar } from "./common/PageLoadingScaffold";
import UnmatchedSmsQueue from "./admin/UnmatchedSmsQueue";
import AdminSettingsDrawer from "./admin/AdminSettingsDrawer";
// Placed here rather than beside AdminTruthBand: PR #478 adds its own import
// on that exact line, and the two collided in the ascending merge (measured
// 2026-09-03). Keep it away from that line.
import DefinitionOfDoneBand from "./admin/DefinitionOfDoneBand";

/* ── Types ─────────────────────────────────────────────────── */

interface ScrapeRunSummary {
  id: string;
  property_id: string;
  property_name: string | null;
  batch_id?: string | null;
  source: string;
  mode: string;
  status: string;
  failure_class: string;
  floor_plans_changed_count: number;
  units_changed_count: number;
  total_floor_plans: number;
  total_units: number;
  property_changed: boolean;
  warning_count: number;
  error_count: number;
  duration_ms: number | null;
  started_at: string;
  completed_at: string | null;
  metadata: any;
  has_special: boolean;
  specials_summary: string | null;
}

interface PropertyScrapeHealth {
  property_id: string;
  property_name: string | null;
  scrape_status: string;
  last_scraped: string | null;
  last_attempted_at: string | null;
  consecutive_failures: number;
  total_floor_plans?: number | null;
  total_units?: number | null;
  has_special: boolean;
  specials_summary: string | null;
}

/* ── Helpers ───────────────────────────────────────────────── */

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return "—";
  const sec = Math.round(ms / 1000);
  const hours = Math.floor(sec / 3600);
  const min = Math.floor((sec % 3600) / 60);
  const remSec = sec % 60;
  if (hours > 0) return `${hours}h ${min}m`;
  if (min > 0) return `${min}m ${remSec}s`;
  return `${remSec}s`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function timeAgo(iso: string | null): string {
  if (!iso) return "Never";
  try {
    const elapsed = Date.now() - new Date(iso).getTime();
    if (elapsed < 0) return "Just now";
    const hours = Math.floor(elapsed / 3600000);
    if (hours < 1) return `${Math.max(1, Math.floor(elapsed / 60000))}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return iso;
  }
}

type ChipTone = "success" | "warning" | "error" | "info" | "default";

function displayLabel(value: string | null | undefined): string {
  const cleaned = (value || "unknown").replace(/_/g, " ").trim();
  if (!cleaned) return "Unknown";
  return cleaned
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function tintedChipSx(tone: ChipTone) {
  return (theme: any) => {
    const baseColor =
      tone === "default" ? theme.palette.grey[500] : theme.palette[tone].main;

    return {
      fontWeight: 500,
      color: tone === "default" ? theme.palette.text.secondary : baseColor,
      backgroundColor: alpha(baseColor, tone === "default" ? 0.1 : 0.16),
      border: "none",
      boxShadow: "none",
      "& .MuiChip-icon": {
        color: tone === "default" ? theme.palette.text.secondary : baseColor,
      },
    };
  };
}

function outlinedChipSx(tone: ChipTone) {
  return (theme: any) => {
    const baseColor =
      tone === "default" ? theme.palette.grey[500] : theme.palette[tone].main;

    return {
      fontWeight: 500,
      color:
        tone === "default"
          ? theme.palette.text.primary
          : theme.palette.mode === "dark"
          ? theme.palette[tone].light
          : theme.palette[tone].dark,
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.02 : 0),
      border: `1px solid ${alpha(
        tone === "default" ? theme.palette.common.white : baseColor,
        tone === "default"
          ? theme.palette.mode === "dark"
            ? 0.18
            : 0.14
          : 0.24
      )}`,
      boxShadow: "none",
      "& .MuiChip-icon": {
        color: "inherit",
      },
    };
  };
}

function statusChipTone(status: string): ChipTone {
  switch (status.toLowerCase()) {
    case "clean":
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "failing":
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function statusChip(status: string) {
  const tone = statusChipTone(status);
  return (
    <Chip
      label={displayLabel(status)}
      size="small"
      sx={(theme) => ({
        ...tintedChipSx(tone)(theme),
        width: "8.25ch",
        justifyContent: "center",
        "& .MuiChip-label": {
          width: "100%",
          px: 1,
          textAlign: "center",
        },
      })}
    />
  );
}

/**
 * PKT-0465 follow-up 2 · The run-source label, named and pinned.
 * A CATCH-ALL, not a map: `lambda` → Lambda, `weekly_local` → Box (the fleet
 * writes weekly_local verbatim — the box inherited the pre-box pipeline's
 * value), anything else → the affirmative "Local". Historical pre-box Mac
 * runs also wrote weekly_local and therefore retro-label as Box — accepted
 * (#378 review): internal admin table, no client-facing decision attached.
 */
export function runSourceLabel(source: string): string {
  return source === "lambda" ? "Lambda" : source === "weekly_local" ? "Box" : "Local";
}

function sourceChip(source: string) {
  const tone: ChipTone = source.toLowerCase() === "lambda" ? "info" : "default";
  return (
    <Chip
      label={displayLabel(source)}
      size="small"
      variant="outlined"
      sx={outlinedChipSx(tone)}
    />
  );
}

function accentChip(
  label: string | number,
  tone: ChipTone,
  variant: "tinted" | "outlined" = "outlined"
) {
  return (
    <Chip
      label={label}
      size="small"
      variant={variant === "tinted" ? "filled" : "outlined"}
      sx={(theme) => ({
        ...(variant === "tinted"
          ? tintedChipSx(tone)(theme)
          : outlinedChipSx(tone)(theme)),
        minWidth: typeof label === "number" ? 32 : undefined,
        fontVariantNumeric: "tabular-nums",
      })}
    />
  );
}

/* ── Main Component ────────────────────────────────────────── */

const ADMIN_STALE_TIME = 60 * 60 * 1000; // 1 hour
const ADMIN_GC_TIME = 30 * 60 * 1000; // 30 min

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const [selectedRun, setSelectedRun] = useState<ScrapeRunSummary | null>(null);
  const [showAllRuns, setShowAllRuns] = usePersistedState<boolean>(
    "admin.showAllRuns",
    false
  );
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [modalTab, setModalTab] = useState(0);
  const [healthSearch, setHealthSearch] = usePersistedState<string>(
    "admin.healthSearch",
    ""
  );
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  // PKT-0308 · when a fix-queue chip is clicked, scope the health modal to that
  // signature's sample properties (reuse the existing modal, don't rebuild).
  const [fixFilterIds, setFixFilterIds] = useState<string[] | null>(null);
  const [fixFilterSig, setFixFilterSig] = useState<string | null>(null);
  const [healthModalTab, setHealthModalTab] = useState<
    "success" | "warning" | "failed" | "unknown"
  >("success");
  const [adminSettingsOpen, setAdminSettingsOpen] = useState(false);

  const {
    data: runsData,
    isLoading: loadingRuns,
    isFetching: fetchingRuns,
    isError: runsIsError,
  } = useQuery({
    queryKey: ["admin", "runs"],
    queryFn: () => fetchWithAuth<{ runs: ScrapeRunSummary[]; total: number }>("/admin/scraper/runs?limit=15"),
    staleTime: ADMIN_STALE_TIME,
    gcTime: ADMIN_GC_TIME,
  });


  const {
    data: healthData,
    isLoading: loadingHealth,
    isFetching: fetchingHealth,
    isError: healthIsError,
  } = useQuery({
    queryKey: ["admin", "health"],
    queryFn: () => fetchWithAuth<{ properties: PropertyScrapeHealth[]; total: number }>("/admin/scraper/property-health?limit=500"),
    staleTime: ADMIN_STALE_TIME,
    gcTime: ADMIN_GC_TIME,
  });

  // PKT-0308 · v2 operational band data (live box_vitals / scraper_fix_queue views).
  const {
    data: vitalsData,
    dataUpdatedAt: vitalsUpdatedAt,
    isLoading: loadingVitals,
    isFetching: fetchingVitals,
    isError: vitalsIsError,
  } = useQuery({
    queryKey: ["admin", "box-vitals"],
    queryFn: () => fetchWithAuth<{ vitals: BoxVitals | null }>("/admin/scraper/vitals"),
    staleTime: 60 * 1000, // ≤60s: the band must track live box state
    gcTime: ADMIN_GC_TIME,
  });
  const {
    data: fixQueueData,
    dataUpdatedAt: fixQueueUpdatedAt,
    isLoading: loadingFixQueue,
    isFetching: fetchingFixQueue,
    isError: fixQueueIsError,
  } = useQuery({
    queryKey: ["admin", "fix-queue"],
    queryFn: () => fetchWithAuth<{ fix_queue: FixQueueRow[] }>("/admin/scraper/fix-queue"),
    staleTime: 60 * 1000,
    gcTime: ADMIN_GC_TIME,
  });

  // PKT-0577 · Fleet actions — the diagnoses the scraper already computes, bucketed into
  // what to actually DO. persist=false is EXPLICIT: this read must never advance the delta
  // baseline (reviewer F2 — a per-browser staleTime guard cannot protect a server write;
  // a hard refresh, a second admin, or a retry all missed it and wrote). The baseline moves
  // only via the explicit "stamp baseline" action (POST /admin/scraper/actions/baseline).
  const {
    data: fleetActionsData,
    isLoading: loadingFleetActions,
    isError: fleetActionsIsError,
    refetch: refetchFleetActions,
  } = useQuery({
    queryKey: ["admin", "fleet-actions"],
    queryFn: () =>
      fetchWithAuth<{ actions: FleetActions | null; reason?: string }>(
        "/admin/scraper/actions?persist=false",
      ),
    staleTime: 15 * 60 * 1000,
    gcTime: ADMIN_GC_TIME,
    refetchOnWindowFocus: false,
  });
  const [fleetActionsBucket, setFleetActionsBucket] = useState<string | null>(null);
  // The ONLY path that advances the delta baseline — an explicit user action, never a load.
  const handleStampBaseline = async () => {
    try {
      await fetchWithAuth("/admin/scraper/actions/baseline", { method: "POST" });
    } finally {
      refetchFleetActions();
    }
  };

  // Fetch property history when a property is selected
  const {
    data: propertyHistoryData,
    isLoading: loadingHistory,
    isError: historyIsError,
  } = useQuery({
    queryKey: ["admin", "property-runs", selectedPropertyId],
    queryFn: () => fetchWithAuth<{ runs: ScrapeRunSummary[]; total: number }>(`/admin/scraper/runs?property_id=${selectedPropertyId}&limit=20`),
    enabled: !!selectedPropertyId,
    staleTime: 5 * 60 * 1000, // 5 min for history
  });

  const runs = runsData?.runs || [];
  const propertyHealth = healthData?.properties || [];
  const propertyHistory = propertyHistoryData?.runs || [];
  const refreshingAdmin =
    fetchingRuns || fetchingHealth || fetchingVitals || fetchingFixQueue;

  const loadData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin"] });
  }, [queryClient]);

  // Group property health rows by status for the modal tabs
  const healthByStatus = React.useMemo(() => {
    const groups: Record<string, PropertyScrapeHealth[]> = {
      success: [],
      warning: [],
      failed: [],
      unknown: [],
    };
    (healthData?.properties || []).forEach((p) => {
      const status = (p.scrape_status || "unknown").toLowerCase();
      if (status === "success" || status === "clean") groups.success.push(p);
      else if (status === "warning") groups.warning.push(p);
      else if (status === "failed" || status === "failing")
        groups.failed.push(p);
      else groups.unknown.push(p);
    });
    return groups;
  }, [healthData]);

  const closeHealthModal = () => {
    setHealthModalOpen(false);
    setFixFilterIds(null);
    setFixFilterSig(null);
  };

  const boxVitals = vitalsData?.vitals ?? null;
  const fixQueue = fixQueueData?.fix_queue ?? [];
  const activeProperties = boxVitals?.active_properties;
  // PKT-0441 · react-query reports dataUpdatedAt === 0 for a query that has
  // NEVER succeeded. `new Date(0).toISOString()` is 1970-01-01 — a fabricated
  // timestamp sitting inside a state object whose entire job is provenance.
  const asOfOrNever = (updatedAt: number) =>
    updatedAt > 0 ? new Date(updatedAt).toISOString() : "never";
  const vitalsAsOf = asOfOrNever(vitalsUpdatedAt);
  const fixQueueAsOf = asOfOrNever(fixQueueUpdatedAt);

  const completionsTruth = numericQueryTruth({
    loading: loadingVitals,
    failed: vitalsIsError,
    source: "box vitals",
    value: boxVitals?.completed_24h,
    of: activeProperties,
    asOf: vitalsAsOf,
  });

  const freshnessTruth = numericQueryTruth({
    loading: loadingVitals,
    failed: vitalsIsError,
    source: "box vitals",
    value: boxVitals?.fresh_72h,
    of: activeProperties,
    asOf: vitalsAsOf,
  });

  const fixQueueHasCounts = fixQueue.every(
    (row) => typeof row.affected_properties === "number"
  );
  const fixQueueAffected = fixQueue.reduce(
    (total, row) => total + row.affected_properties,
    0
  );
  // PKT-0441 F3 · the numerator is the fix queue, but the denominator
  // (active_properties) comes from BOX VITALS. Declare that split so a
  // vitals failure names box vitals instead of blaming the fix queue, which
  // in that scenario answered perfectly.
  const fixQueueTruth = numericQueryTruth({
    loading: loadingFixQueue,
    failed: fixQueueIsError,
    source: "scraper fix queue",
    value: fixQueueHasCounts ? fixQueueAffected : undefined,
    of: activeProperties,
    ofSource: "box vitals",
    ofLoading: loadingVitals,
    ofFailed: vitalsIsError,
    asOf: fixQueueAsOf,
  });

  // Fix-queue chip → scope the EXISTING health modal to that signature's sample
  // properties (the view exposes up to 3 sample_property_ids per signature).
  const handleFixQueueSelect = (row: FixQueueRow) => {
    setFixFilterIds(row.sample_property_ids ?? []);
    setFixFilterSig(row.failure_signature);
    setHealthModalTab("failed");
    setHealthModalOpen(true);
  };

  // Health-modal body rows, scoped by an active fix-queue filter.
  const modalHealthRows: PropertyScrapeHealth[] = (
    healthByStatus[healthModalTab] || []
  ).filter((p) => !fixFilterIds || fixFilterIds.includes(p.property_id));

  const filteredHealth = propertyHealth.filter((p) => {
    if (!healthSearch) return true;
    const search = healthSearch.toLowerCase();
    return (
      (p.property_name || "").toLowerCase().includes(search) ||
      p.property_id.toLowerCase().includes(search) ||
      p.scrape_status.toLowerCase().includes(search)
    );
  });

  const handleHealthRowClick = (p: PropertyScrapeHealth) => {
    setSelectedRun(null);
    setSelectedPropertyId(p.property_id);
    setSelectedPropertyName(p.property_name || p.property_id.slice(0, 8));
    setModalTab(1); // Open on History tab
  };

  const handleRunClick = (run: ScrapeRunSummary) => {
    setSelectedRun(run);
    setSelectedPropertyId(run.property_id);
    setSelectedPropertyName(run.property_name || run.property_id.slice(0, 8));
    setModalTab(0); // Open on Run Details tab
  };

  const handleHealthTableWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (event.deltaY <= 0) return;

      const pageMaxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const remainingPageScroll = Math.max(0, pageMaxScroll - window.scrollY);

      if (remainingPageScroll <= 1) return;

      event.preventDefault();

      const pageDelta = Math.min(event.deltaY, remainingPageScroll);
      window.scrollBy({ top: pageDelta, behavior: "auto" });

      const leftoverDelta = event.deltaY - pageDelta;
      if (leftoverDelta > 0) {
        event.currentTarget.scrollTop += leftoverDelta;
      }
    },
    []
  );

  const closeModal = () => {
    setSelectedRun(null);
    setSelectedPropertyId(null);
    setSelectedPropertyName("");
    setModalTab(0);
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <PageHeaderTitle icon={<AdminPanelSettingsIcon />} title="Admin Dashboard">
          <Chip label="Admin Only" color="primary" size="small" />
        </PageHeaderTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Admin settings — role defaults + unsubscribes">
            <IconButton
              onClick={() => setAdminSettingsOpen(true)}
              size="small"
              aria-label="Open admin settings"
              data-tour="admin-settings-btn"
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>
          <PageRefreshButton iconOnly loading={Boolean(refreshingAdmin)} onClick={loadData} />
        </Box>
      </Box>

      {/* Unified top-bar — debounced 150ms · matches PropertiesPage look (Agent 25 · 2026-04-25) */}
      <LoadingTopBar
        loading={
          loadingRuns ||
          loadingHealth ||
          loadingVitals ||
          loadingFixQueue
        }
        refreshing={Boolean(refreshingAdmin)}
      />

      {/* PKT-0308 · v2 operational band — "is the box working right now + what
          needs me". Sits above the existing tiles/modals (preserved below as the
          drill-down layer). Fix-queue chips reuse the property-health modal. */}
      {/* TRAIN-4 · unmatched inbound texts — customers the dispatcher could
          not attribute. D6: admin-visible, never silently dropped. */}
      <UnmatchedSmsQueue />

      {/* Ch76 · first consumer of /analytics/admin-truth — money + lead figures
          under ONE declared filter set, envelope-aware (unknown renders as
          unknown, never as a confident zero). Self-fetching by design: this
          band must not join the Promise.all it exists to retire. */}
      {/* Ch76 · the Definition-of-Done bar (RATIFIED 2026-09-01): eight lanes
          + the 30-day streak, read from /admin/definition-of-done. A lane whose
          instrument is not wired says the WORD unknown (amber), never a verdict.
          Sits above the money band; self-fetching for the same reason. */}
      <DefinitionOfDoneBand />

      <AdminTruthBand />

      {/* Ch76 · the Instagram DM ledger (PR #474) on a screen: counts, last
          times, the two heartbeats incl. the token identity probe, latest
          rows. Unknown renders as the amber word. Self-fetching, same rule. */}
      <InstagramDmLedgerCard />

      <OperationalBand
        completions={completionsTruth}
        freshness={freshnessTruth}
        fixQueueAffected={fixQueueTruth}
        fixQueueRows={fixQueue}
        onRetry={loadData}
        onSelectFixSignature={handleFixQueueSelect}
      />

      {/* PKT-0577 · Fleet actions — "what do I DO about it", next to the band that
          says what's happening. Summary here; per-property detail behind a click,
          because a 200-row list on the dashboard is a list nobody reads. */}
      <FleetActionsBand
        actions={fleetActionsData?.actions ?? null}
        reason={fleetActionsData?.reason ?? null}
        loading={loadingFleetActions}
        isError={fleetActionsIsError}
        onRetry={() => refetchFleetActions()}
        onSelectBucket={setFleetActionsBucket}
        onStampBaseline={handleStampBaseline}
      />

      <FleetActionsModal
        actions={fleetActionsData?.actions ?? null}
        bucket={fleetActionsBucket}
        onClose={() => setFleetActionsBucket(null)}
        onSelectBucket={setFleetActionsBucket}
      />

      {/* Recent Runs Table */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Recent Scrape Runs
      </Typography>
      {runsIsError ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Couldn't read recent scrape runs. Retry from the page header.
        </Alert>
      ) : isMobile ? (
        <Card variant="outlined" sx={{ mb: 3, overflow: "hidden" }}>
          {loadingRuns ? (
            // Skeleton rows match the actual run-row layout so the load
            // feels continuous instead of a spinner placeholder.
            <Box>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: i < 2 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Skeleton animation="wave" variant="text" width="55%" height={18} />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    width="35%"
                    height={14}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </Box>
          ) : runs.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No scrape runs found.
              </Typography>
            </Box>
          ) : (() => {
            const visibleRuns = showAllRuns ? runs : runs.slice(0, 3);
            const hiddenCount = runs.length - visibleRuns.length;
            return (
              <>
                {visibleRuns.map((run, idx) => (
                  <Box
                    key={run.id}
                    onClick={() => handleRunClick(run)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      cursor: "pointer",
                      borderBottom:
                        idx < visibleRuns.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : "none",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {run.property_name || run.property_id.slice(0, 8)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatTimestamp(run.started_at)}
                        {run.duration_ms !== null && ` · ${formatDuration(run.duration_ms)}`}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>{statusChip(run.status)}</Box>
                  </Box>
                ))}
                {hiddenCount > 0 && (
                  <Box
                    onClick={() => setShowAllRuns(true)}
                    sx={{
                      py: 1.25,
                      textAlign: "center",
                      cursor: "pointer",
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    Show more
                  </Box>
                )}
                {showAllRuns && runs.length > 3 && (
                  <Box
                    onClick={() => setShowAllRuns(false)}
                    sx={{
                      py: 1.25,
                      textAlign: "center",
                      cursor: "pointer",
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      color: "text.secondary",
                      fontWeight: 500,
                      fontSize: "0.8125rem",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.text.primary, 0.04),
                      },
                    }}
                  >
                    Show less
                  </Box>
                )}
              </>
            );
          })()}
        </Card>
      ) : (
        <TableContainer component={Card} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Floor Plans</TableCell>
                <TableCell>Units</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRuns ? (
                // Skeleton rows match table cell shape for unified shimmer
                [0, 1, 2].map((i) => (
                  <TableRow key={`runs-skel-${i}`}>
                    {[0, 1, 2, 3, 4, 5, 6].map((c) => (
                      <TableCell key={c}>
                        <Skeleton animation="wave" variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No scrape runs found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <TableRow
                    key={run.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRunClick(run)}
                  >
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {run.property_name || run.property_id.slice(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(run.status)}</TableCell>
                    <TableCell>{sourceChip(runSourceLabel(run.source))}</TableCell>
                    <TableCell>{run.total_floor_plans}</TableCell>
                    <TableCell>{run.total_units}</TableCell>
                    <TableCell>{formatDuration(run.duration_ms)}</TableCell>
                    <TableCell>{formatTimestamp(run.started_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Property Health Table */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Property Scrape Health{loadingHealth || healthIsError ? "" : ` (${filteredHealth.length})`}
        </Typography>
        <TextField
          size="small"
          placeholder="Search properties..."
          value={healthSearch}
          onChange={(e) => setHealthSearch(e.target.value)}
          disabled={loadingHealth || healthIsError}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ width: { xs: "100%", sm: 250 } }}
        />
      </Box>
      {isMobile ? (
        <Card
          variant="outlined"
          onWheel={handleHealthTableWheel}
          sx={{
            maxHeight: 600,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: 0 },
            scrollbarWidth: "none",
          }}
        >
          {loadingHealth ? (
            // Skeleton rows match property-health-row shape
            <Box>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: i < 3 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Skeleton animation="wave" variant="text" width="70%" height={18} />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    width="40%"
                    height={14}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </Box>
          ) : healthIsError ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              Couldn't read property health. Retry from the page header.
            </Alert>
          ) : filteredHealth.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {healthSearch ? "No matching properties." : "No scrape health data available."}
              </Typography>
            </Box>
          ) : (
            filteredHealth.map((p, idx) => (
              <Box
                key={p.property_id}
                onClick={() => handleHealthRowClick(p)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  cursor: "pointer",
                  borderBottom:
                    idx < filteredHealth.length - 1
                      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                      : "none",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {p.property_name || p.property_id.slice(0, 8)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Last scraped: {timeAgo(p.last_scraped)}
                    {p.consecutive_failures > 0 && ` · ${p.consecutive_failures} fail${p.consecutive_failures === 1 ? "" : "s"}`}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0 }}>{statusChip(p.scrape_status)}</Box>
              </Box>
            ))
          )}
        </Card>
      ) : (
        <TableContainer
          component={Card}
          variant="outlined"
          onWheel={handleHealthTableWheel}
          sx={{
            maxHeight: 600,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: 0 },
            scrollbarWidth: "none",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Special</TableCell>
                <TableCell>Last Scraped</TableCell>
                <TableCell>Last Attempted</TableCell>
                <TableCell>Failures</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHealth ? (
                [0, 1, 2, 3].map((i) => (
                  <TableRow key={`health-skel-${i}`}>
                    {[0, 1, 2, 3, 4, 5].map((c) => (
                      <TableCell key={c}>
                        <Skeleton animation="wave" variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : healthIsError ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="warning">
                      Couldn't read property health. Retry from the page header.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : filteredHealth.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {healthSearch ? "No matching properties." : "No scrape health data available."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHealth.map((p) => (
                  <TableRow
                    key={p.property_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleHealthRowClick(p)}
                  >
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {p.property_name || p.property_id.slice(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(p.scrape_status)}</TableCell>
                    <TableCell>
                      <Tooltip title={p.specials_summary || ""} arrow disableHoverListener={!p.has_special}>
                        <Chip
                          label={p.has_special ? "Yes" : "No"}
                          size="small"
                          color={p.has_special ? "success" : "default"}
                          variant={p.has_special ? "filled" : "outlined"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{timeAgo(p.last_scraped)}</TableCell>
                    <TableCell>{timeAgo(p.last_attempted_at)}</TableCell>
                    <TableCell>
                      {p.consecutive_failures > 0 ? (
                        accentChip(p.consecutive_failures, "error")
                      ) : (
                        "0"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Run Detail / Property History Modal */}
      <Dialog
        open={!!selectedRun || !!selectedPropertyId}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {(selectedRun || selectedPropertyId) && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight={600}>
                {selectedPropertyName}
              </Typography>
              <IconButton onClick={closeModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Tabs value={modalTab} onChange={(_, v) => {
              if (v === 0 && !selectedRun && propertyHistory.length > 0) {
                setSelectedRun(propertyHistory[0]);
              }
              setModalTab(v);
            }} sx={{ px: 3 }}>
              <Tab label="Run Details" />
              <Tab label="Property History" />
            </Tabs>
            <DialogContent>
              {/* Tab 0: Run Details */}
              {modalTab === 0 && selectedRun && (
              <Box>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                {statusChip(selectedRun.status)}
                <Chip
                  // PKT-0468 · THE THIRD CALL SITE. This one never used the
                  // helper at all, so a box run read "Local Batch" here while
                  // both tables correctly read "Box". Counting call sites by
                  // grepping `runSourceLabel` finds two and misses this — the
                  // question is how many places RENDER a run source, not how
                  // many already call the helper.
                  label={runSourceLabel(selectedRun.source)}
                  size="small"
                  color={selectedRun.source === "lambda" ? "info" : "default"}
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Run Details</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}><Typography variant="body2">Started:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">{formatTimestamp(selectedRun.started_at)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Duration:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">{formatDuration(selectedRun.duration_ms)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Property Changed:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">{selectedRun.property_changed ? "Yes" : "No"}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Leasing Special:</Typography></Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color={selectedRun.has_special ? "success.main" : "text.secondary"}>
                    {selectedRun.has_special ? "Yes" : "No"}
                  </Typography>
                  {selectedRun.has_special && selectedRun.specials_summary && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {selectedRun.specials_summary}
                    </Typography>
                  )}
                </Grid>
                {selectedRun.failure_class && selectedRun.failure_class !== "none" && (
                  <>
                    <Grid item xs={6}><Typography variant="body2">Failure Class:</Typography></Grid>
                    <Grid item xs={6}><Typography variant="body2" color="error">{selectedRun.failure_class}</Typography></Grid>
                  </>
                )}
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {(() => {
                const recon = selectedRun.metadata?.reconciliation || {};
                const capturedFp = recon.capture_floorplans_total ?? 0;
                const capturedUnits = recon.capture_units_total ?? 0;
                const parsedFp = recon.parsed_floorplans_total ?? selectedRun.total_floor_plans;
                const parsedUnits = recon.parsed_units_total ?? selectedRun.total_units;
                const fpMissed = capturedFp - parsedFp;
                const unitsMissed = capturedUnits - parsedUnits;
                const fpChanged = selectedRun.floor_plans_changed_count;
                const unitsChanged = selectedRun.units_changed_count;

                return (
                  <Box sx={{ mb: 2 }}>
                    {/* Capture */}
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Website Capture</Typography>
                      <Typography variant="body2">
                        Found <b>{capturedFp} floor plans</b> and <b>{capturedUnits} units</b> on the website
                      </Typography>
                    </Box>

                    {/* Parse */}
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Parser Results</Typography>
                      <Typography variant="body2">
                        Extracted <b>{parsedFp} floor plans</b> and <b>{parsedUnits} units</b> from HTML
                      </Typography>
                      {(fpMissed > 0 || unitsMissed > 0) && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                          {fpMissed > 0 && <>{fpMissed} floor plan{fpMissed > 1 ? "s" : ""} couldn't be parsed. </>}
                          {unitsMissed > 0 && <>{unitsMissed} unit{unitsMissed > 1 ? "s" : ""} couldn't be parsed.</>}
                        </Typography>
                      )}
                    </Box>

                    {/* Database */}
                    <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Database Updates</Typography>
                      <Typography variant="body2">
                        {fpChanged > 0 || unitsChanged > 0 ? (
                          <><b>{fpChanged} floor plan{fpChanged !== 1 ? "s" : ""}</b> and <b>{unitsChanged} unit{unitsChanged !== 1 ? "s" : ""}</b> updated in database</>
                        ) : (
                          <>No changes — data matched previous scrape</>
                        )}
                      </Typography>
                      {(selectedRun.warning_count > 0 || selectedRun.error_count > 0) && (
                        <Typography variant="body2" color={selectedRun.error_count > 0 ? "error.main" : "warning.main"} sx={{ mt: 0.5 }}>
                          {selectedRun.warning_count > 0 && <>{selectedRun.warning_count} warning{selectedRun.warning_count > 1 ? "s" : ""}. </>}
                          {selectedRun.error_count > 0 && <>{selectedRun.error_count} error{selectedRun.error_count > 1 ? "s" : ""}.</>}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })()}

              {selectedRun.metadata?.validation?.issues?.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Validation Issues</Typography>
                  {selectedRun.metadata.validation.issues.map((issue: any, i: number) => (
                    <Alert key={i} severity={issue.level === "error" ? "error" : "warning"} sx={{ mb: 1 }}>
                      {issue.message}
                    </Alert>
                  ))}
                </>
              )}
              </Box>
              )}

              {/* Tab 0 fallback: no run selected yet */}
              {modalTab === 0 && !selectedRun && (
                loadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : historyIsError ? (
                  <Alert severity="warning" sx={{ my: 1 }}>
                    Couldn't read scrape history for this property. Retry from the
                    page header.
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No run data available. Check the Property History tab.
                  </Typography>
                )
              )}

              {/* Tab 1: Property History */}
              {modalTab === 1 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Scrape History
                </Typography>
                {loadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : historyIsError ? (
                  // PKT-0441 F1 · a failed read is not an empty history. Without
                  // this branch `propertyHistoryData?.runs` degrades to `[]` and
                  // the modal states "No scrape history for this property." —
                  // the exact false-zero this packet exists to eliminate.
                  <Alert severity="warning" sx={{ my: 1 }}>
                    Couldn't read scrape history for this property. Retry from the
                    page header.
                  </Alert>
                ) : propertyHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No scrape history for this property.
                  </Typography>
                ) : isMobile ? (
                  <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                    {propertyHistory.map((run, idx, arr) => (
                      <Box
                        key={run.id}
                        onClick={() => {
                          setSelectedRun(run);
                          setModalTab(0);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          cursor: "pointer",
                          borderBottom:
                            idx < arr.length - 1
                              ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                              : "none",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            ),
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {formatTimestamp(run.started_at)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {formatDuration(run.duration_ms)}
                            {run.property_changed ? " · changed" : " · no changes"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>{statusChip(run.status)}</Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Floor Plans</TableCell>
                          <TableCell>Units</TableCell>
                          <TableCell>Changed</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {propertyHistory.map((run) => (
                          <TableRow
                            key={run.id}
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedRun(run);
                              setModalTab(0);
                            }}
                          >
                            <TableCell>{statusChip(run.status)}</TableCell>
                            <TableCell>{sourceChip(runSourceLabel(run.source))}</TableCell>
                            <TableCell>{run.total_floor_plans}</TableCell>
                            <TableCell>{run.total_units}</TableCell>
                            <TableCell>
                              {run.property_changed ? (
                                accentChip("Yes", "success")
                              ) : "No"}
                            </TableCell>
                            <TableCell>{formatDuration(run.duration_ms)}</TableCell>
                            <TableCell>{formatTimestamp(run.started_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Health Tabs Modal */}
      <Dialog
        open={healthModalOpen}
        onClose={closeHealthModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Property Health
            {fixFilterSig && (
              <Chip
                size="small"
                color="warning"
                label={`fix-queue: ${fixFilterSig.replace(/_/g, " ")} · ${modalHealthRows.length} shown`}
                onDelete={() => {
                  setFixFilterIds(null);
                  setFixFilterSig(null);
                }}
              />
            )}
          </Box>
          <IconButton onClick={closeHealthModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={healthModalTab}
          onChange={(_, v) => setHealthModalTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}
        >
          <Tab
            value="success"
            label={healthIsError ? "Success" : `Success (${healthByStatus.success.length})`}
          />
          <Tab
            value="warning"
            label={healthIsError ? "Warning" : `Warning (${healthByStatus.warning.length})`}
          />
          <Tab
            value="failed"
            label={healthIsError ? "Failed" : `Failed (${healthByStatus.failed.length})`}
          />
          <Tab
            value="unknown"
            label={healthIsError ? "Unknown" : `Unknown (${healthByStatus.unknown.length})`}
          />
        </Tabs>
        <DialogContent>
          {loadingHealth ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : healthIsError ? (
            <Alert severity="warning">
              Couldn't read property health. Retry from the page header.
            </Alert>
          ) : modalHealthRows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No properties in this status.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box>
              {modalHealthRows.map((p, idx, arr) => (
                <Box
                  key={p.property_id}
                  onClick={() => {
                    setHealthModalOpen(false);
                    handleHealthRowClick(p);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom:
                      idx < arr.length - 1
                        ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                        : "none",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {p.property_name || p.property_id.slice(0, 8)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Last scraped: {timeAgo(p.last_scraped)}
                      {p.consecutive_failures > 0 &&
                        ` · ${p.consecutive_failures} fail${p.consecutive_failures === 1 ? "" : "s"}`}
                    </Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>{statusChip(p.scrape_status)}</Box>
                </Box>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Special</TableCell>
                    <TableCell>Last Scraped</TableCell>
                    <TableCell>Floor Plans</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell>Consecutive Failures</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalHealthRows.map((p) => (
                    <TableRow
                      key={p.property_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setHealthModalOpen(false);
                        handleHealthRowClick(p);
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 280 }}
                        >
                          {p.property_name || p.property_id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(p.scrape_status)}</TableCell>
                      <TableCell>
                        <Tooltip title={p.specials_summary || ""} arrow disableHoverListener={!p.has_special}>
                          <Chip
                            label={p.has_special ? "Yes" : "No"}
                            size="small"
                            color={p.has_special ? "success" : "default"}
                            variant={p.has_special ? "filled" : "outlined"}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {timeAgo(p.last_scraped)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {p.total_floor_plans ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {p.total_units ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.consecutive_failures > 0 ? (
                          accentChip(p.consecutive_failures, "error")
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <AdminSettingsDrawer
        open={adminSettingsOpen}
        onClose={() => setAdminSettingsOpen(false)}
      />
    </Box>
  );
};

export default AdminDashboard;
